import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clientAPI, adviceAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1a365d',
    marginBottom: '0.5rem',
  },
  subtitle: {
    color: '#718096',
    fontSize: '1rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  actionCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  actionIcon: {
    fontSize: '2rem',
    marginBottom: '1rem',
  },
  actionTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#1a365d',
    marginBottom: '0.5rem',
  },
  actionDescription: {
    color: '#718096',
    fontSize: '0.9rem',
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#1a365d',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid #e2e8f0',
  },
  caseItem: {
    padding: '1rem',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  caseTitle: {
    fontWeight: '500',
    color: '#2d3748',
    marginBottom: '0.25rem',
  },
  caseMeta: {
    fontSize: '0.85rem',
    color: '#718096',
  },
  badge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  badgeOpen: {
    backgroundColor: '#c6f6d5',
    color: '#276749',
  },
  badgeInProgress: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },
  badgeClosed: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    color: '#718096',
  },
  profileCard: {
    display: 'flex',
    gap: '1.5rem',
    alignItems: 'flex-start',
  },
  profileAvatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    color: '#a0aec0',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#1a365d',
    marginBottom: '0.25rem',
  },
  profileDetail: {
    fontSize: '0.9rem',
    color: '#718096',
    marginBottom: '0.25rem',
  },
};

const ClientDashboard = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [cases, setCases] = useState([]);
  const [adviceRequests, setAdviceRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casesRes, adviceRes] = await Promise.all([
        clientAPI.getMyCases(),
        adviceAPI.getMyRequests(),
      ]);
      setCases(casesRes.data);
      setAdviceRequests(adviceRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeStyle = (status) => {
    switch (status) {
      case 'open': return styles.badgeOpen;
      case 'in_progress': return styles.badgeInProgress;
      default: return styles.badgeClosed;
    }
  };

  if (loading) {
    return <div style={styles.container}>{t('common.loading')}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>{t('dashboard.welcome')}, {profile?.full_name || t('dashboard.client')}</h1>
        <p style={styles.subtitle}>{t('dashboard.clientSubtitle')}</p>
      </div>

      <div style={styles.grid}>
        <Link
          to="/find-attorney"
          style={styles.actionCard}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
        >
          <div style={styles.actionIcon}>&#128269;</div>
          <h3 style={styles.actionTitle}>{t('clientDashboard.findAttorneyTitle')}</h3>
          <p style={styles.actionDescription}>{t('clientDashboard.findAttorneyDesc')}</p>
        </Link>

        <Link
          to="/submit-case"
          style={styles.actionCard}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
        >
          <div style={styles.actionIcon}>&#128221;</div>
          <h3 style={styles.actionTitle}>{t('clientDashboard.submitCaseTitle')}</h3>
          <p style={styles.actionDescription}>{t('clientDashboard.submitCaseDesc')}</p>
        </Link>

        <Link
          to="/request-advice"
          style={styles.actionCard}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
        >
          <div style={styles.actionIcon}>&#128172;</div>
          <h3 style={styles.actionTitle}>{t('clientDashboard.getLegalAdviceTitle')}</h3>
          <p style={styles.actionDescription}>{t('clientDashboard.getLegalAdviceDesc')}</p>
        </Link>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{t('clientDashboard.yourProfile')}</h2>
        <div style={styles.profileCard}>
          <div style={styles.profileAvatar}>&#128100;</div>
          <div style={styles.profileInfo}>
            <div style={styles.profileName}>{profile?.full_name || t('dashboard.notSet')}</div>
            <div style={styles.profileDetail}>{t('clientDashboard.phone')}: {profile?.phone || t('dashboard.notSet')}</div>
            <div style={styles.profileDetail}>{t('clientDashboard.location')}: {profile?.location || t('dashboard.notSet')}</div>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{t('clientDashboard.yourCases')} ({cases.length})</h2>
        {cases.length === 0 ? (
          <div style={styles.emptyState}>
            <p>{t('dashboard.noCasesYet')}</p>
            <Link to="/submit-case" style={{ color: '#3182ce' }}>{t('dashboard.submitYourFirst')}</Link>
          </div>
        ) : (
          cases.map(c => (
            <Link to={`/cases/${c.id}`} key={c.id} style={{ textDecoration: 'none' }}>
              <div style={styles.caseItem}>
                <div>
                  <div style={styles.caseTitle}>{c.title}</div>
                  <div style={styles.caseMeta}>
                    {c.practice_area} &bull; {new Date(c.created_at).toLocaleDateString()}
                    {c.response_count > 0 && ` • ${c.response_count} ${t('clientDashboard.responses')}`}
                  </div>
                </div>
                <span style={{ ...styles.badge, ...getBadgeStyle(c.status) }}>
                  {t(`statuses.${c.status}`)}
                </span>
              </div>
            </Link>
          ))
        )}
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{t('clientDashboard.adviceRequests')} ({adviceRequests.length})</h2>
        {adviceRequests.length === 0 ? (
          <div style={styles.emptyState}>
            <p>{t('dashboard.noAdviceRequests')}</p>
            <Link to="/request-advice" style={{ color: '#3182ce' }}>{t('dashboard.requestAdvice')}</Link>
          </div>
        ) : (
          adviceRequests.map(ar => (
            <div key={ar.id} style={styles.caseItem}>
              <div>
                <div style={styles.caseTitle}>{ar.subject}</div>
                <div style={styles.caseMeta}>
                  ${ar.payment_amount} &bull; {new Date(ar.created_at).toLocaleDateString()}
                </div>
              </div>
              <span style={{
                ...styles.badge,
                ...(ar.payment_status === 'paid' ? styles.badgeOpen : styles.badgeInProgress)
              }}>
                {t(`statuses.${ar.payment_status}`)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
